import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import { useState, useEffect, useCallback } from 'react';
import Api from '../api';
import { RateSelectorConfigResponse } from '../api/Api';

// ============================================
// DEFAULT CONFIG (fallback if API fails)
// ============================================

/**
 * Default rate selector configuration.
 * Used as fallback if API call fails or during initial load.
 * These values match the original hardcoded constants.
 */
export const DEFAULT_RATE_SELECTOR_CONFIG: RateSelectorConfigResponse = {
    bottomRateMultiplier: 0.9,
    baseTopRateMultiplier: 1.15,
    newGradThresholdYears: 2,
    newGradAdjustment: -5,
    newGradColorCompression: 0.6,
    statusAdjustments: {
        0: 0,   // Active — no bonus
        1: 2,   // Pro — +$2 to top rate
        2: 4,   // Elite — +$4 to top rate
    },
    colorThresholds: {
        0: { greenMax: 1.07, yellowMax: 1.12 },
        1: { greenMax: 1.09, yellowMax: 1.14 },
        2: { greenMax: 1.10, yellowMax: 1.16 },
    },
    orangeCount: 2,
    maxDeltaCap: 12,
    configVersion: 0,
};

// ============================================
// LOCAL STORAGE CACHING
// ============================================

const CACHE_KEY = 'rateSelectorConfig';
const CACHE_TIMESTAMP_KEY = 'rateSelectorConfigTimestamp';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

interface CachedConfig {
    config: RateSelectorConfigResponse;
    timestamp: number;
}

/**
 * Read config from localStorage cache
 */
function readCachedConfig(): CachedConfig | null {
    try {
        const configStr = safeLocalStorage.getItem(CACHE_KEY);
        const timestampStr = safeLocalStorage.getItem(CACHE_TIMESTAMP_KEY);
        
        if (!configStr || !timestampStr) return null;
        
        const config = JSON.parse(configStr) as RateSelectorConfigResponse;
        const timestamp = parseInt(timestampStr, 10);
        
        if (!config || !timestamp || isNaN(timestamp)) return null;
        
        return { config, timestamp };
    } catch (e) {
        return null;
    }
}

/**
 * Write config to localStorage cache
 */
function writeCachedConfig(config: RateSelectorConfigResponse): void {
    try {
        safeLocalStorage.setItem(CACHE_KEY, JSON.stringify(config));
        safeLocalStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (e) {
        // Silent fail - localStorage may be unavailable
    }
}

/**
 * Check if cached config is still fresh (within TTL)
 */
function isCacheFresh(cached: CachedConfig | null): boolean {
    if (!cached) return false;
    const age = Date.now() - cached.timestamp;
    return age < CACHE_TTL_MS;
}

/**
 * Clear the cached config (useful for testing or forced refresh)
 */
export function clearRateSelectorConfigCache(): void {
    try {
        safeLocalStorage.removeItem(CACHE_KEY);
        safeLocalStorage.removeItem(CACHE_TIMESTAMP_KEY);
    } catch (e) {
        // Silent fail
    }
}

// ============================================
// SINGLETON PROMISE FOR DEDUPLICATION
// ============================================

let fetchPromise: Promise<RateSelectorConfigResponse> | null = null;

/**
 * Fetch config from API with deduplication.
 * Multiple simultaneous calls will share the same promise.
 */
async function fetchConfigFromApi(): Promise<RateSelectorConfigResponse> {
    // Return existing promise if already fetching
    if (fetchPromise) {
        return fetchPromise;
    }
    
    fetchPromise = (async () => {
        try {
            const config = await Api.getRateSelectorConfig();
            writeCachedConfig(config);
            return config;
        } catch (error) {
            console.warn('Failed to fetch rate selector config, using defaults:', error);
            // Return cached config if available, otherwise defaults
            const cached = readCachedConfig();
            return cached?.config ?? DEFAULT_RATE_SELECTOR_CONFIG;
        } finally {
            // Clear the singleton after completion
            fetchPromise = null;
        }
    })();
    
    return fetchPromise;
}

// ============================================
// MAIN HOOK
// ============================================

interface UseRateSelectorConfigResult {
    config: RateSelectorConfigResponse;
    loading: boolean;
    error: Error | null;
    refresh: () => Promise<void>;
}

/**
 * Hook to get rate selector configuration with caching.
 * 
 * Caching Strategy:
 * 1. On mount, return cached config immediately if available (for instant render)
 * 2. If cache is stale (> 1 hour), fetch fresh config in background
 * 3. If no cache, fetch from API
 * 4. If API fails, use defaults as fallback
 * 
 * @returns Object with config, loading state, error, and refresh function
 */
export function useRateSelectorConfig(): UseRateSelectorConfigResult {
    const [config, setConfig] = useState<RateSelectorConfigResponse>(() => {
        // Initialize with cached config or defaults for immediate render
        const cached = readCachedConfig();
        return cached?.config ?? DEFAULT_RATE_SELECTOR_CONFIG;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    
    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const freshConfig = await fetchConfigFromApi();
            setConfig(freshConfig);
        } catch (e) {
            setError(e instanceof Error ? e : new Error('Failed to fetch config'));
        } finally {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => {
        const cached = readCachedConfig();
        
        // If cache is fresh, no need to fetch
        if (isCacheFresh(cached)) {
            return;
        }
        
        // Fetch fresh config (either cache is stale or missing)
        refresh();
    }, [refresh]);
    
    return { config, loading, error, refresh };
}

// ============================================
// SYNCHRONOUS CONFIG ACCESS
// ============================================

/**
 * Get rate selector config synchronously.
 * Returns cached config if available, otherwise defaults.
 * 
 * Use this for synchronous operations where you can't await.
 * The config will be the most recently fetched value.
 */
export function getRateSelectorConfigSync(): RateSelectorConfigResponse {
    const cached = readCachedConfig();
    return cached?.config ?? DEFAULT_RATE_SELECTOR_CONFIG;
}

/**
 * Pre-fetch rate selector config.
 * Call this early in app initialization to warm the cache.
 * Returns a promise that resolves when config is fetched.
 */
export async function prefetchRateSelectorConfig(): Promise<RateSelectorConfigResponse> {
    const cached = readCachedConfig();
    
    // Return cached if fresh (isCacheFresh returns false for null, so cached is guaranteed non-null here)
    if (cached && isCacheFresh(cached)) {
        return cached.config;
    }
    
    // Otherwise fetch fresh
    return fetchConfigFromApi();
}

